import React, { FC, useEffect, useState } from 'react';
import { Button, Col, Layout, message, Row, Tooltip } from 'antd';
import { useMediaPredicate } from 'react-media-hook';
import { useHistory } from 'react-router';
import { WarningCircle } from 'common/svg';
import { Loader } from 'common/components/Loader';
import { priceFormatter } from 'common/helpers/format.helper';
import { PageHeader } from 'common/components/Page/PageHeader';
import { PageHeaderMob } from 'common/components/Page/PageHeaderMob';
import { PageContent } from 'common/components/Page/PageContent';
import { PageFooter } from 'common/components/Page/PageFooter';
import { MEDIA_MOBILE_768 } from 'common/const/common.const';
import { ESidebarMenuKey, ESidebarMenuLabel, sidebarMenuIcons } from 'common/models/commonModel';
import { ERoutesFranchiseePrivate, RoutesCirPrefix } from 'common/models/routesModel';
import { PageStoreSelector } from 'common/components/PageStoreSelector';
import { isSuperAdminRole } from 'common/helpers/permission.helper';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import {
  createPurchaseOrderModel,
  getPurchaseOrderOutstandingPayablesModel,
  getPurchaseOrderRecoBasisModel,
  getPurchaseOrderRecoModel,
  getPurchaseOrderStoreCollection,
} from 'app/store/reducers/purchaseOrder.reducer';
import { IPurchaseOrderCreatePayload, IPurchaseOrderStoreModel } from 'entities/PurchaseOrder/PurchaseOrder.models';
import { PurchaseOrderConfirmModal } from 'entities/PurchaseOrder/components/PurchaseOrderConfirmModal';
import { GeneratePOTable, clearDraftFromStorage } from 'entities/PurchaseOrder/components/GeneratePOTable';
import { PurchaseOrderRecoBasis } from 'entities/PurchaseOrder/components/PurchaseOrderRecoBasis';
import {
  getPurchaseOrderClosedMessage,
  isPurchaseOrderRecoOrderable,
  toPhtMoment,
} from 'entities/PurchaseOrder/PurchaseOrder.helper';

export const GeneratePOPage: FC = () => {
  const dispatch = useAppDispatch();

  const {
    purchaseOrderModelLoading,
    purchaseOrderRecoModelLoading,
    purchaseOrderRecoModel,
    purchaseOrderRecoModelProducts,
    purchaseOrderOutstandingPayablesModel,
    purchaseOrderStoreCollection,
  } = useAppSelector((state) => state.purchaseOrder);
  const authUser = useAppSelector((state) => state.auth.authUser);

  const [store, setStore] = useState<IPurchaseOrderStoreModel | undefined>(undefined);
  const [openPurchaseOrderConfirmModal, setOpenPurchaseOrderConfirmModal] = useState<boolean>(false);
  const [openPurchaseOrderRecoBasis, setOpenPurchaseOrderRecoBasis] = useState<boolean>(false);
  const history = useHistory();
  const isMobile: boolean = useMediaPredicate(MEDIA_MOBILE_768);

  useEffect(() => {
    dispatch(getPurchaseOrderStoreCollection());
  }, []);

  // MBC-85: when navigated from Track PO RECO column, pre-select the store via router state.
  useEffect(() => {
    const navState = (history.location.state || {}) as { storeCode?: string; location?: string } | null;
    if (!navState?.storeCode || !navState?.location || !purchaseOrderStoreCollection?.data?.length) {
      return;
    }
    const matched = purchaseOrderStoreCollection.data.find(
      (s) => s.code === navState.storeCode && s.location === navState.location
    );
    if (matched) {
      setStore(matched);
    }
  }, [purchaseOrderStoreCollection]);

  // MBC-82: bind Outstanding Payables to selected store; refetch on store change.
  useEffect(() => {
    if (!store) {
      return;
    }
    dispatch(getPurchaseOrderOutstandingPayablesModel({ storeCode: store.code as string }));
  }, [store]);

  const isSuperAdmin = isSuperAdminRole(authUser?.role);
  // Delivery date, selling period and cutoff are null when the store cannot order now (cutoffReason set).
  const deliveryScheduleMoment = toPhtMoment(purchaseOrderRecoModel?.deliverySchedule);
  const deliverySchedule = deliveryScheduleMoment
    ? `${deliveryScheduleMoment.format('MM/DD/YYYY')} (${deliveryScheduleMoment.format('dddd')})`
    : '-';
  // MBC-76: render the selling period in PHT (+08:00, no DST) — same as Delivery Schedule.
  // Plain dateFormatter formats in the browser TZ and shifts the dates by a day.
  const sellingStartMoment = toPhtMoment(purchaseOrderRecoModel?.sellingPeriod.startDate);
  const sellingEndMoment = toPhtMoment(purchaseOrderRecoModel?.sellingPeriod.endDate);
  const sellingPeriod =
    sellingStartMoment && sellingEndMoment
      ? `${sellingStartMoment.format('MM/DD/YYYY')} to ${sellingEndMoment.format(
          'MM/DD/YYYY'
        )} (${sellingStartMoment.format('dddd')} - ${sellingEndMoment.format('dddd')})`
      : '-';

  const cutoffMoment = toPhtMoment(purchaseOrderRecoModel?.cutoffAt);
  const isPastCutoffComputed = !!cutoffMoment && cutoffMoment.valueOf() <= Date.now();
  const isOrderable = isPurchaseOrderRecoOrderable(purchaseOrderRecoModel);
  // Ordering closed (WINDOW_CLOSED / NOT_YOUR_DELIVERY_DAY): there is no delivery date to submit.
  const closedMessage = getPurchaseOrderClosedMessage(purchaseOrderRecoModel);
  const cutoffText = closedMessage ?? (cutoffMoment ? `Order by ${cutoffMoment.format('ddd MM/DD HH:mm')} PHT` : null);
  const isSubmitDisabled = isSuperAdmin || isPastCutoffComputed || !isOrderable;
  const submitDisabledReason = isSuperAdmin
    ? 'Super Admin cannot confirm purchase orders.'
    : closedMessage
    ? closedMessage
    : isPastCutoffComputed
    ? 'Cutoff for selected delivery date has passed. Page will refresh shortly.'
    : '';

  const changeStore = (value?: string | number) => {
    const store = purchaseOrderStoreCollection?.data.find((item) => item.code === value);
    setStore(store);
  };

  const openConfirmModal = () => {
    setOpenPurchaseOrderConfirmModal(true);
  };

  const closeConfirmModal = () => {
    setOpenPurchaseOrderConfirmModal(false);
  };

  const openRecoBasis = async (): Promise<void> => {
    const defaultProduct = purchaseOrderRecoModel?.products[0];

    if (!store || !defaultProduct) {
      return;
    }

    await dispatch(
      getPurchaseOrderRecoBasisModel({
        storeCode: store.code,
        location: store.location,
        prodCode: defaultProduct.prodCode,
      })
    );

    setOpenPurchaseOrderRecoBasis(true);
  };

  const closeRecoBasis = (): void => {
    setOpenPurchaseOrderRecoBasis(false);
  };

  const submitPurchaseOrder = async () => {
    if (!store || !purchaseOrderRecoModel?.deliverySchedule) {
      return;
    }

    // MBC-76: race guard — if cutoff slipped between render and click, force refetch
    // (next-day RECO + new cutoff) instead of submitting a stale deliveryDate.
    if (isPastCutoffComputed) {
      dispatch(getPurchaseOrderRecoModel({ storeCode: store.code, location: store.location }));
      return;
    }

    const payload: IPurchaseOrderCreatePayload = {
      storeCode: store.code,
      location: store.location,
      deliveryType: 'delivery',
      deliveryDate: purchaseOrderRecoModel.deliverySchedule,
      // MBC-78: spec requires ORDER > 0 per item; defensively filter out zeroed/undefined rows.
      // @ts-ignore
      items: purchaseOrderRecoModelProducts
        .filter((item) => item.orderCount > 0)
        .map((item) => {
          return {
            productCode: item.prodCode,
            count: item.orderCount,
          };
        }),
    };

    const result = await dispatch(createPurchaseOrderModel(payload));

    if (!result.success) {
      message.error(result.message ?? 'Failed to submit the purchase order. Please try again.');

      return;
    }

    clearDraftFromStorage(store.code);
    history.push(`${RoutesCirPrefix}${ERoutesFranchiseePrivate.TrackPO}`);
  };

  return (
    <Layout className="relative height-full flex flex__column">
      {purchaseOrderRecoModelLoading && <Loader />}

      {isMobile ? (
        <PageHeaderMob
          title={
            <div className="flex flex-align-center">
              {sidebarMenuIcons[ESidebarMenuKey.GeneratePO]}
              <span className="ml-3 ff-regular">{ESidebarMenuLabel[ESidebarMenuKey.GeneratePO]}</span>
            </div>
          }
          extra={[
            <PageStoreSelector
              key="storeSelector"
              stores={purchaseOrderStoreCollection?.data}
              onChange={changeStore}
            />,
          ]}
        />
      ) : (
        <PageHeader
          title="Generate Purchase Order"
          extra={<PageStoreSelector stores={purchaseOrderStoreCollection?.data} onChange={changeStore} />}
        />
      )}

      <PageContent>
        <div className="page-content__info">
          <Row gutter={[{ xs: 16, md: 32 }, 12]}>
            <Col>
              <div className="mb-3 page-content__info-label">Delivery Schedule</div>
              <span className="flex flex-align-center ff-regular fs-xxs color-purple">
                {deliverySchedule}
                <Tooltip
                  overlayInnerStyle={{ fontSize: 12, color: '#97A2BB' }}
                  title="Holidays/special events may affect actual delivery date"
                >
                  <div className="ml-2">
                    <WarningCircle className="icon-purple" />
                  </div>
                </Tooltip>
              </span>
            </Col>
            <Col>
              <div className="mb-3 page-content__info-label">Selling Period</div>
              <span className="ff-regular fs-xxs">{sellingPeriod}</span>
            </Col>
            {!!cutoffText && (
              <Col>
                <div className="mb-3 page-content__info-label">Cutoff</div>
                <span className={`ff-regular fs-xxs ${!isOrderable || isPastCutoffComputed ? 'color-orange' : ''}`}>
                  {cutoffText}
                </span>
              </Col>
            )}
            {!!purchaseOrderOutstandingPayablesModel?.totalSum && (
              <Col>
                <div className="mb-3 page-content__info-label">Outstanding Payables</div>
                <span className="flex flex-align-center ff-regular fs-xxs color-orange">
                  {priceFormatter(purchaseOrderOutstandingPayablesModel.totalSum)}
                  <Tooltip
                    overlayClassName="color-gray-1"
                    overlayInnerStyle={{ fontSize: 12, color: '#97A2BB' }}
                    title="Pay this apart from the order amount"
                  >
                    <div className="ml-2">
                      <WarningCircle className="icon-orange" />
                    </div>
                  </Tooltip>
                </span>
              </Col>
            )}
          </Row>
        </div>

        <div className="page-content__container">
          <GeneratePOTable
            store={store}
            disabled={isSubmitDisabled}
            disabledReason={submitDisabledReason}
            disableOrderCount={isSuperAdmin}
            onConfirm={openConfirmModal}
            onViewRecoBasis={openRecoBasis}
          />
        </div>
      </PageContent>

      {isMobile && !!purchaseOrderRecoModelProducts.length && (
        <PageFooter>
          <Row gutter={8}>
            <Col span={12}>
              <Button className="btn-secondary h-44 width-full" onClick={openRecoBasis}>
                View Reco Basis
              </Button>
            </Col>
            <Col span={12}>
              <Tooltip title={submitDisabledReason}>
                <Button className="btn-primary h-44 width-full" onClick={openConfirmModal} disabled={isSubmitDisabled}>
                  Confirm Order
                </Button>
              </Tooltip>
            </Col>
          </Row>
        </PageFooter>
      )}

      <PurchaseOrderConfirmModal
        open={openPurchaseOrderConfirmModal}
        loading={purchaseOrderModelLoading}
        disabled={isSubmitDisabled}
        disabledReason={submitDisabledReason}
        onCancel={closeConfirmModal}
        onSubmit={submitPurchaseOrder}
      />

      <PurchaseOrderRecoBasis
        open={openPurchaseOrderRecoBasis}
        store={store}
        products={purchaseOrderRecoModel?.products}
        onCancel={closeRecoBasis}
      />
    </Layout>
  );
};
